import { Attrs, EventListeners, MinzeElement, Reactive } from 'minze'
import MainModule from '../../modules/main'
import { isMobile } from '../../modules/utils'

// TODO: Add loading state
export interface TabItem {
  title: string
  tabContent: string
  total?: number | ''
  tabIcon?: string
  isEmojiLikeVariant?: boolean
}

export interface LvcTabGroup {
  items: string
  initialTabIndex: number
  loading?: boolean
  navigation?: boolean
  geolocButton?: boolean
  globalPadding?: boolean
  mobileBehaviour?: string
  activeTabIndex: number
  selectedTab?: {
    tabIcon?: string
    isEmojiLikeVariant?: boolean
    text?: string
    counter?: string | number
  }
}

export class LvcTabGroup extends MinzeElement {
  // Declare props to accept the `items` array dynamically
  attrs: Attrs = [
    'geoloc-button',
    'navigation',
    'items',
    'loading',
    ['initial-tab-index', 0],
    ['global-padding', true],
    ['mobile-behaviour', 'scroll'],
  ]

  static observedAttributes = ['loading', 'items']

  reactive: Reactive = [
    ['activeTabIndex', 0],
    'selectedTab',
    ['mobileSelectOpen', false],
    ['isMobile', isMobile()],
    'isTabStuck',
  ]
  handleWindowResize = () => {
    this.isMobile = isMobile()
  }
  onReady = () => {
    this.activeTabIndex = +this.initialTabIndex || 0
    this.selectedTab = {
      tabIcon: (JSON.parse(decodeURIComponent(this.items)) as TabItem[])[this.activeTabIndex]
        .tabIcon,
      isEmojiLikeVariant: (JSON.parse(decodeURIComponent(this.items)) as TabItem[])[
        this.activeTabIndex
      ].isEmojiLikeVariant,
      text: (JSON.parse(decodeURIComponent(this.items)) as TabItem[])[this.activeTabIndex].title,
      counter: (JSON.parse(decodeURIComponent(this.items)) as TabItem[])[this.activeTabIndex].total,
    }
    this.handleSticky()
  }
  onRender = () => {
    const activeTabElement = this.select('.tab-title.active')
    if (activeTabElement) {
      activeTabElement.scrollIntoView({
        behavior: 'smooth',
        inline: 'center',
        block: 'nearest',
      })
    }
  }
  trackGtmTabTitle = () => {
    // Ensure that this event is only tracked on the homepage
    if (window.location.pathname !== '/') return
    try {
      const tabTitle =
        ((JSON.parse(decodeURIComponent(this.items)) as TabItem[])?.[
          this.activeTabIndex
        ]?.title).trim() || ''
      const mainModule = MainModule.getInstance()
      mainModule.analyticsTrack('accueil_grid_onglet', {
        link_text: tabTitle,
      })
    } catch (error) {
      console.error('Error tracking GTM event:', error)
    }
  }
  async handleDropdownClick(event: Event) {
    if (!event.target) return
    const target = event.target as HTMLElement
    const index = Number(target.getAttribute('data-index'))
    const item = this.select(`.content > [data-index="${index}"]`)
    if (!item) return

    this.mobileSelectOpen = false
    this.selectedTab = {
      tabIcon: (JSON.parse(decodeURIComponent(this.items)) as TabItem[])[index].tabIcon,
      isEmojiLikeVariant: (JSON.parse(decodeURIComponent(this.items)) as TabItem[])[index]
        .isEmojiLikeVariant,
      text: (JSON.parse(decodeURIComponent(this.items)) as TabItem[])[index].title,
      counter: (JSON.parse(decodeURIComponent(this.items)) as TabItem[])[index].total,
    }

    if (this.mobileBehaviour === 'scroll') {
      window.scrollTo({
        top: item.getBoundingClientRect().top + window.scrollY - 101,
        behavior: 'smooth',
      })
    }

    this.activeTabIndex = index
    this.trackGtmTabTitle()
  }
  async handleTabClick(event: Event) {
    if (!event.target) return
    const target = event.target as HTMLElement
    const index = Number(target.getAttribute('data-index'))
    this.activeTabIndex = index
    this.scrollToTopOfTabContent()
    this.handleSticky()
    this.trackGtmTabTitle()
  }

  handleTabPrev = () => {
    if (this.activeTabIndex === 0) return
    this.activeTabIndex -= 1
    this.scrollToTopOfTabContent()
    this.handleSticky()
    this.trackGtmTabTitle()
  }

  handleTabNext = () => {
    const itemsLength = JSON.parse(decodeURIComponent(this.items)).length
    if (this.activeTabIndex === itemsLength - 1) return
    this.activeTabIndex += 1
    this.scrollToTopOfTabContent()
    this.handleSticky()
    this.trackGtmTabTitle()
  }
  scrollToTopOfTabContent = () => {
    const tabs = this.select('.tabs')
    if (!tabs) return

    const pos = tabs.getBoundingClientRect().top - 101
    if (this.isTabStuck) {
      window.scrollTo({
        top: pos + window.scrollY,
        behavior: 'smooth',
      })
    }
  }
  handleSticky = () => {
    const tabTitles = this.select('.tab-titles')
    if (!tabTitles) return
    if (tabTitles.getBoundingClientRect().top <= 100) {
      this.isTabStuck = true
    } else {
      this.isTabStuck = false
    }
  }
  handleScroll = (e: Event) => {
    if (!e) return
    this.handleSticky()
  }
  renderTabArrowLeft = () => {
    return /*html*/ `
      <div class="tab-arrow__left">
        ${this.isTabStuck ? stickyArrowIcon() : defaultArrowIcon()}
      </div>
    `
  }
  renderTabArrowRight = () => {
    return /*html*/ `
      <div class="tab-arrow__right">
        ${this.isTabStuck ? stickyArrowIcon() : defaultArrowIcon()}
      </div>
    `
  }

  renderDownArrow = () => {
    return /*html*/ `
      <div class="dropdown-arrow">
        <svg width="13" height="8" viewBox="0 0 13 8" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M12.684 0.345511C12.9971 0.522357 13.0939 0.897481 12.9002 1.18338C12.4503 1.84741 11.0725 3.33539 9.81171 4.6176C9.17181 5.26835 8.54375 5.88454 8.05152 6.34061C7.80664 6.56751 7.58795 6.7617 7.4148 6.90218C7.32971 6.97122 7.24381 7.03696 7.16457 7.08883C7.1258 7.11421 7.07468 7.14552 7.01663 7.17299L7.01544 7.17355C6.97928 7.19068 6.84472 7.25439 6.66662 7.25439C6.47652 7.25439 6.33326 7.18091 6.29999 7.16385L6.29719 7.16242C6.23979 7.13318 6.18865 7.09997 6.1495 7.07294C6.06913 7.01744 5.97975 6.94628 5.8892 6.87026C5.70516 6.71574 5.46946 6.50142 5.204 6.25184C4.67085 5.7506 3.98832 5.07939 3.30021 4.394C2.73361 3.82965 2.16745 3.26005 1.6735 2.76311C0.952369 2.03761 0.38516 1.46696 0.195261 1.29357C-0.065087 1.05587 -0.065087 0.670467 0.195261 0.432759C0.455608 0.195052 0.877715 0.195052 1.13806 0.432759C1.34906 0.625412 1.96583 1.24579 2.72204 2.00642C3.20833 2.49556 3.75228 3.0427 4.28307 3.57139C4.96995 4.25555 5.64157 4.91584 6.1605 5.40372C6.36002 5.5913 6.53271 5.74936 6.67263 5.87186C6.79246 5.76873 6.93679 5.63905 7.10182 5.48615C7.57447 5.04821 8.18759 4.44702 8.81958 3.80431C10.1026 2.49959 11.3914 1.0963 11.7663 0.542961C11.96 0.257067 12.3708 0.168665 12.684 0.345511Z" fill="white"/>
        </svg>
      </div>
    `
  }

  renderMobile = () => /*html*/ `
    ${
      this.geolocButton
        ? /* html */ `
      <div class="mobile-geo-button">
        <lvc-geo-button></lvc-geo-button>
      </div>
    `
        : ''
    }
    <div class="mobile-tabs">
      <div class="content">
        <div class="mobile-dropdown">
          <div class="dropdown-selected-item">
            <div class="dropdown-selected-item-text-wrapper">
              ${this.selectedTab?.tabIcon ? `<img class="tab-icon ${this.selectedTab?.isEmojiLikeVariant ? 'emoji-like-variant' : ''} is-selected" src="${this.selectedTab?.tabIcon}" alt="${this.selectedTab?.text}" />` : ''}
              <div class="dropdown-selected-item-text">${this.selectedTab?.text}</div>
              <div class="mobile-counter-text is-selected">${this.selectedTab?.counter}</div>
            </div>
            ${this.renderDownArrow()}
          </div>
          <div class="mobile-dropdown-drawer">
            ${(JSON.parse(decodeURIComponent(this.items)) as TabItem[])
              .filter(item => item.title !== this.selectedTab)
              .map(
                (item, index) => `
              <div class="mobile-dropdown-link" data-index=${index}>
                ${item.tabIcon ? `<img class="tab-icon ${item.isEmojiLikeVariant ? 'emoji-like-variant' : ''}" src="${item.tabIcon}" alt="${item.title}" />` : ''}
                ${item.title}
                <sup class="mobile-counter-text">${item.total}</sup>
              </div>
            `
              )
              .join('')}
            </div>
        </div>
        <div class="mobile-dropdown-spacer"></div>
        ${(JSON.parse(decodeURIComponent(this.items)) as TabItem[])
          .map(
            (item, index) => `
          <div class="mobile-content-item ${index === this.activeTabIndex ? 'active' : ''}" data-index="${index}" data-testid="${item.title}">
            ${item.tabContent}
          </div>
        `
          )
          .join('')}
      </div>
    </div>
  `

  renderDesktopTabs = () => /*html*/ `
    <div class="tabs ${this.globalPadding ? 'padding-global' : ''}">
      <div class="tab-titles">
        ${
          this.navigation
            ? `<button class="tab-nav" id="tab-prev">
                ${this.renderTabArrowLeft()}
              </button>`
            : ''
        }
        ${
          this.navigation
            ? `<button class="tab-nav" id="tab-next">
                ${this.renderTabArrowRight()}
              </button>`
            : ''
        }
        <div class="tab-titles-wrapper">
          ${(JSON.parse(decodeURIComponent(this.items)) as TabItem[])
            .map(
              (item, index) => `
                <div class="tab-title ${index === this.activeTabIndex ? 'active' : ''}" data-index="${index}" data-testid="${item.title}">
                  ${item.tabIcon ? `<img class="tab-icon ${item.isEmojiLikeVariant ? 'emoji-like-variant' : ''}" src="${item.tabIcon}" alt="${item.title}" />` : ''}
                  ${item.title}
                  <div class="tab-counter">${item.total}</div>
                </div>
              `
            )
            .join('')}
        </div>
       
        ${this.geolocButton ? `<lvc-geo-button ${this.isTabStuck ? 'theme="alternative"' : ''}></lvc-geo-button>` : ''}
      </div>
      <div class="tab-content">
        ${(JSON.parse(decodeURIComponent(this.items)) as TabItem[])
          .map(
            (item, index) => `
            <div class="tab-panel ${index === this.activeTabIndex ? 'active' : ''}" data-index="${index}">
              ${item.tabContent}
            </div>
          `
          )
          .join('')}
      </div>
    </div>
  `
  // Dynamic rendering function
  // TODO: Change tab system to slider?
  html = () => `
    ${
      this.items
        ? `
    ${this.isMobile ? this.renderMobile() : this.renderDesktopTabs()}
    `
        : ''
    }
  `

  css = () => `
   .padding-global {
      padding-left: 6rem;
      padding-right: 6rem;
  }
  @media screen and (max-width: 991px) {
      .padding-global {
        padding-left: 4.5rem;
        padding-right: 4.5rem;
      }
    }
    @media screen and (max-width: 767px) {
      .padding-global {
        padding-left: 1rem;
        padding-right: 1rem;
      }
    }
  .tab-arrow__left, .tab-arrow__right {
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .tab-arrow__right {
    transform: rotate(180deg);
  }
  .mobile-dropdown-spacer {
    padding-top: 3rem;
  }
  .mobile-dropdown-drawer {
    position: absolute;
    display: ${this.mobileSelectOpen ? 'flex' : 'none'};
    gap: 1rem;
    flex-direction: column;
    padding: 1rem;
    background-color: #1E007D;
    height: auto;
    width: 100%;
    left: 0;
    right: 0;
    top: calc(100% + 4px);
    border-radius: 12px;

  }
  .mobile-dropdown {
    margin-left: ${this.globalPadding ? '1rem' : '0'};
    margin-right: ${this.globalPadding ? '1rem' : '0'};
    box-shadow: 0px 8px 16px rgba(0, 0, 0, 0.25);
    position: sticky;
    -webkit-position: sticky;
    top: 1rem;
    z-index: 99;
    
    border-radius: 12px;
  }
  .mobile-counter-text {
    font-size: 0.75rem;
    margin-left: 4px;
  }
  .mobile-counter-text.is-selected {
    margin-left: 5px;
    margin-top: -17px;
  }
  .dropdown-arrow {
    transform: ${this.mobileSelectOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  }
    .dropdown-selected-item {
      color: white;
      display:flex;
      align-items: center;
      justify-content: space-between;
      font-size: 1.5rem;
      cursor: pointer;
    }
    .dropdown-selected-item-text-wrapper {
      display: flex;
      align-items: center;
      line-clamp: 1;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .dropdown-selected-item-text {
      -webkit-line-clamp: 1;
      line-clamp: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      text-wrap: nowrap;
    }
    .mobile-dropdown-link {
      display: flex;
      align-items: center;
      cursor: pointer;
    }
    .mobile-dropdown-link .mobile-counter-text {
      align-self: flex-start;
    }
    .mobile-tabs {
      display: none;
    }
    .mobile-content-item {
      display: ${this.mobileBehaviour === 'scroll' ? 'block' : 'none'};
      padding-left: ${this.globalPadding ? '1rem' : '0'};
      padding-right: ${this.globalPadding ? '1rem' : '0'};
      overflow: hidden;
      position: relative;
    }
    .mobile-content-item.active {
      display: block;
    }

    .tab-titles-wrapper {
      padding: ${this.navigation ? '0 3rem' : '0'};
      display: flex;
      align-items: center;
      position: relative;
      gap: ${this.isTabStuck ? '2.5rem' : '1.875rem'};
      overflow-x: auto;
      overflow-y: hidden;
      scrollbar-width: none;
    }

    lvc-geo-button {
      margin-left: auto;
    }

    .tab-nav {
      z-index: 2;
      position: absolute;
      cursor: pointer;
      border: 0;
      background: none;
      height: 50%;
      width: 60px;
      display: flex;
      align-items: center;
    }

    #tab-prev {
      justify-content: start;
      left: ${this.isTabStuck ? '0px' : '-6px'};
    }

    #tab-prev > .tab-arrow__left {
      opacity: ${this.activeTabIndex === 0 ? 0.35 : 1};
    }

    #tab-next {
      justify-content: end;
      right: 225px;
    }

    #tab-next >.tab-arrow__right {
      opacity: ${
        this.activeTabIndex === JSON.parse(decodeURIComponent(this.items))?.length - 1 ? 0.35 : 1
      };
    }

    .tab-titles {
      font-weight: 500;
      position: sticky;
      top: 100px;
      display: flex;
      align-items: center;
      gap: 56px;
      z-index: 99;
      border-bottom: ${this.isTabStuck ? 'none' : '1px solid #1E007D'};
      padding: ${this.isTabStuck ? '0 0.5rem' : '0'};
      box-shadow: ${this.isTabStuck ? '0px 8px 16px rgba(0, 0, 0, 0.25)' : 'none'};
      border-radius: ${this.isTabStuck ? '12px' : '0'};
      background-color: ${this.isTabStuck ? '#1E007D' : 'transparent'};
      font-size: ${this.isTabStuck ? '0.875rem' : '1.125rem'};
      color: ${this.isTabStuck ? 'rgba(255,255,255,0.5)' : '#1E007D'};
    }
    /* .tab-titles.stuck > #tab-prev, .tab-titles.stuck > #tab-next {
      background: none;
      color: white;
    } */
    /* .tab-titles.stuck > .tab-titles-wrapper > .tab-title.active {
      color: #BFF0FF;
      font-size: 18px;
    }
    .tab-titles.stuck > .tab-titles-wrapper > .tab-title {
      color: rgba(255, 255, 255, 0.5);
      font-size: 0.875rem;
    } */
    .tab-title {
      white-space: nowrap;
      cursor: pointer;
      padding: 14px 0;
      line-height: 32px;
      display: flex;
      align-items: center;
      gap: 3px;
      border: none;
      background: none;
      border-bottom: ${this.isTabStuck ? 'none' : '4px solid transparent'};
      transition: border-bottom 0.2s;
    }
    .tab-icon {
      width: 32px;
      height: 32px;
      border-radius: 100%;
      overflow: hidden;
      margin-right: 7px;
    }
    .tab-icon.emoji-like-variant {
      width: 18px;
      height: 18px;
      border-radius: 0;
      margin-right: 0;
    }

    .tab-title:hover {
      border-bottom: 4px solid rgba(30, 0, 125, 0.5);
    }

    .tab-counter {
      line-height: 1.4;
      align-self: flex-start;
      font-size: 0.75rem;
    }

    .tab-title.active {
      font-size: ${this.isTabStuck ? '1.125rem' : 'inherit'};
      color: ${this.isTabStuck ? '#BFF0FF' : 'inherit'};
      border-bottom: ${this.isTabStuck ? '0' : '4px solid #1E007D'};
    }

    .tab-content {
      overflow: hidden;
      margin-top: 4rem;
    }

    .tab-panel {
      display: none;
    }

    .tab-panel.active {
      display: block;
    }
    .mobile-dropdown {
      padding: 20px;
      ${this.selectedTab?.tabIcon && !this.selectedTab?.isEmojiLikeVariant ? `padding-left: 0.5rem;` : ''}
      background-color: #1E007D;
      color: white;
    }
    .mobile-geo-button {
        display: none;
      }
    @media only screen and (max-width: 991px) {
      .tab-icon {
        width: 28px;
        height: 28px;
      }
      .tab-icon.emoji-like-variant {
        margin-right: 3px;
      }
      .mobile-tabs {
        display: block;
      }
      .tabs {
        display: none;
      }
      .dropdown-selected-item {
        font-size: 1.25rem;
      }
      .mobile-geo-button {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0 2rem;
        margin-bottom: 1.5rem;
      }
    }
    @media only screen and (max-width: 767px) {
      .mobile-dropdown {
        margin-left: 1rem;
        margin-right: 1rem;
      }
    }
  `

  eventListeners?: EventListeners = [
    ['#tab-prev', 'click', this.handleTabPrev],
    ['#tab-next', 'click', this.handleTabNext],
    ['.tab-title', 'click', e => this.handleTabClick(e)],
    ['.mobile-dropdown-link', 'click', e => this.handleDropdownClick(e)],
    ['.dropdown-selected-item', 'click', e => (this.mobileSelectOpen = !this.mobileSelectOpen)],
    [window, 'resize', this.handleWindowResize],
    [window, 'scroll', this.handleScroll],
  ]
}

const defaultArrowIcon = () => `
  <svg width="20" height="20" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect y="0.5" width="20" height="20" rx="10" fill="#1E007D"/>
    <path d="M11.2002 6.9001C10.3047 7.84978 9.2767 8.8945 8.34685 10.0272C8.13667 10.2832 8.12209 10.6472 8.31253 10.9183C9.14517 12.1032 9.97408 12.9885 11.1162 14.1001" stroke="white" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>
`

const stickyArrowIcon = () => `
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="0.5" y="0.5" width="19" height="19" rx="9.5" fill="#BFF0FF" stroke="#1E007D"/>
    <path d="M11.2002 6.39961C10.3047 7.3493 9.2767 8.39401 8.34685 9.52668C8.13667 9.7827 8.12209 10.1468 8.31253 10.4178C9.14517 11.6027 9.97408 12.488 11.1162 13.5996" stroke="#1E007D" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>

`
